import React, { useCallback, useContext, useEffect, useState } from "react";
import api from "../api";
import { REPORT_KEY } from "../modules";
import { PermsContext } from "../perms";
import { downloadReport, downloadPath } from "../download";

const fmt = (v, col) => {
  if (v === null || v === undefined || v === "") return <span style={{ color: "#94a3b8" }}>—</span>;
  if (col.type === "bool") return v === 1 || v === true ? <span className="pill ok">Yes</span> : <span className="pill">No</span>;
  if (col.type === "date") { const d = new Date(v); return isNaN(d) ? String(v) : d.toLocaleDateString("en-GB"); }
  if (col.key === "risk" || col.key === "severity" || col.key === "priority" || col.key === "risk_level") {
    const s = String(v).toLowerCase();
    const cls = s.includes("very") || s === "high" ? "danger" : s === "medium" ? "warn" : "ok";
    return <span className={`pill ${cls}`}>{v}</span>;
  }
  if (col.key === "status") { const s = String(v).toLowerCase(); const cls = /close|active|resolved|disposed/.test(s) ? "ok" : /open|review|pending|submit/.test(s) ? "warn" : ""; return <span className={`pill ${cls}`}>{v}</span>; }
  return String(v);
};

function Form({ module, initial, wf, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    const f = {};
    module.columns.forEach((c) => { if (c.form !== false) f[c.key] = initial ? initial[c.key] ?? "" : c.type === "bool" ? false : ""; });
    return f;
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const cols = module.columns.filter((c) => c.form !== false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      if (wf) {
        // Maker-checker: submit the change for approval instead of applying it.
        const title = `${initial?.id ? "Update" : "New"} — ${module.title}: ${form[module.columns[0].key] || ""}`;
        await api.post("/workflow/requests", { module: module.key, action: initial?.id ? "update" : "create", entity_id: initial?.id || null, title, payload: form });
        onSaved("Submitted for approval.");
      } else {
        if (initial?.id) await api.put(`${module.endpoint}/${initial.id}`, form);
        else await api.post(module.endpoint, form);
        onSaved();
      }
    } catch (e2) { setErr(e2?.response?.data?.message || "Save failed"); setSaving(false); }
  };

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="modal" onSubmit={submit}>
        <h3>{initial ? "Edit" : "New"} — {module.title}</h3>
        <div className="grid2">
          {cols.map((c) => (
            <div className="form-row" key={c.key} style={c.type === "textarea" ? { gridColumn: "1 / -1" } : null}>
              <label>{c.label}</label>
              {c.type === "textarea" ? (
                <textarea value={form[c.key] ?? ""} onChange={(e) => set(c.key, e.target.value)} />
              ) : c.type === "bool" ? (
                <select value={form[c.key] ? "1" : "0"} onChange={(e) => set(c.key, e.target.value === "1")}>
                  <option value="0">No</option><option value="1">Yes</option>
                </select>
              ) : c.type === "select" ? (
                <select value={form[c.key] ?? ""} onChange={(e) => set(c.key, e.target.value)}>
                  <option value="">— select —</option>
                  {c.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input type={c.type === "number" ? "number" : c.type === "date" ? "date" : "text"}
                  value={form[c.key] ?? ""} onChange={(e) => set(c.key, e.target.value)} />
              )}
            </div>
          ))}
        </div>
        {err && <div className="err">{err}</div>}
        <div className="foot">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn primary" disabled={saving}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

export default function Register({ module }) {
  const perms = useContext(PermsContext);
  const can = perms.actions;
  const wf = (perms.workflowModules || []).includes(module.key); // maker-checker on?
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // row | 'new' | null
  const pageSize = 25;
  const listCols = module.columns.filter((c) => c.list !== false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(module.endpoint, { params: { page, pageSize, q } });
      const d = res.data?.data;
      if (Array.isArray(d)) { setRows(d); setTotal(d.length); }
      else { setRows(d?.rows || []); setTotal(d?.total || 0); }
    } catch { setRows([]); setTotal(0); }
    setLoading(false);
  }, [module.endpoint, page, q]);

  useEffect(() => { load(); }, [load]);

  const del = async (row) => {
    if (wf) {
      if (!window.confirm("Submit a delete request for approval?")) return;
      await api.post("/workflow/requests", { module: module.key, action: "delete", entity_id: row.id, title: `Delete — ${module.title} #${row.id}` });
      alert("Delete submitted for approval.");
      return;
    }
    if (!window.confirm("Delete this record?")) return;
    await api.delete(`${module.endpoint}/${row.id}`);
    load();
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      <h1>{module.title}</h1>
      <p className="sub">{total} record{total === 1 ? "" : "s"}{wf && <span className="pill warn" style={{ marginLeft: 8 }}>Maker-checker: changes require approval</span>}</p>
      <div className="card">
        <div className="toolbar">
          <input placeholder="Search…" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} />
          {REPORT_KEY[module.key] && can.export && (
            <>
              <button className="btn sm" title="Export CSV" onClick={() => downloadReport(REPORT_KEY[module.key], "csv", { q })}>CSV</button>
              <button className="btn sm" title="Export Excel" onClick={() => downloadReport(REPORT_KEY[module.key], "xlsx", { q })}>Excel</button>
              <button className="btn sm" title="Export PDF" onClick={() => downloadReport(REPORT_KEY[module.key], "pdf", { q })}>PDF</button>
            </>
          )}
          {!module.readOnly && can.create && <button className="btn primary" onClick={() => setEditing("new")}>+ Add</button>}
        </div>
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                {listCols.map((c) => <th key={c.key}>{c.label}</th>)}
                {!module.readOnly && <th style={{ width: 120 }}></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  {listCols.map((c) => <td key={c.key}>{fmt(r[c.key], c)}</td>)}
                  {!module.readOnly && (
                    <td style={{ whiteSpace: "nowrap" }}>
                      {can.export && (module.rowActions || []).map((ra) => {
                        const { path, filename } = ra.build(r);
                        return <button key={ra.label} className="btn link sm" onClick={() => downloadPath(path, filename)}>{ra.label}</button>;
                      })}
                      {(wf ? can.create : can.edit) && <button className="btn link sm" onClick={() => setEditing(r)}>Edit</button>}
                      {(wf ? can.create : can.delete) && <button className="btn link sm danger" onClick={() => del(r)}>Delete</button>}
                    </td>
                  )}
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={listCols.length + 1}><div className="empty">{loading ? "Loading…" : "No records yet."}</div></td></tr>}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="pager">
            <button className="btn sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
            <span>Page {page} / {pages}</span>
            <button className="btn sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        )}
      </div>
      {editing && (
        <Form module={module} initial={editing === "new" ? null : editing} wf={wf}
          onClose={() => setEditing(null)} onSaved={(msg) => { setEditing(null); if (msg) alert(msg); load(); }} />
      )}
    </div>
  );
}
